import React, { MouseEventHandler, useEffect, useState, useMemo } from 'react'
import Box from '@material-ui/core/Box'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { offeringTagDeleteThunk, offeringTagListThunk, selectCurrentTempleOfferingTags, selectOfferingTagStatus } from 'store/reducers/temple/offeringTagSlice'
import IntlMessages from '@crema/utility/IntlMessages'
import Paper from '@material-ui/core/Paper'
import { RouteComponentProps } from 'react-router'
import { apiStatus } from 'shared/constants/AppEnum'
import LinearProgress from '@material-ui/core/LinearProgress'
import { IOfferingTagResponse } from 'types/api/temple'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import RedButton from 'components/Button/RedButton'
import usePermissionCheck from 'hooks/usePermissionCheck'
import { PERMISSIONS, RESTRICTED_PATHS } from 'utils/permissions'
import OfferingTagTable from 'components/Temple/OfferingTagTable'
import { offeringListThunk, selectCurrentTempleOfferings } from 'store/reducers/temple/offeringSlice'
import OfferingTagToolbar, { OfferingTagFilterForm } from './OfferingTagToolbar'

type OfferingTagDeleteGroup = {
    id: string
    name: string
    tags: IOfferingTagResponse[]
}

const ListOfferingTag: React.FC<RouteComponentProps> = ({ history, location }) => {
    const dispatch = useAppDispatch()
    const { pathname } = location
    const offeringTags = useAppSelector(selectCurrentTempleOfferingTags)
    const offerings = useAppSelector(selectCurrentTempleOfferings)
    const api = useAppSelector(selectOfferingTagStatus)
    const [deleteItem, setDeleteItem] = useState<OfferingTagDeleteGroup | null>(null)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [deleteIds, setDeleteIds] = useState<(string | number)[]>([])
    const permissions = usePermissionCheck(RESTRICTED_PATHS.OFFERING_TAGS)
    const filters_ = location.search ? new URLSearchParams(location.search) : undefined
    const [filters, setFilters] = useState<OfferingTagFilterForm>({ category: filters_?.get('category') ?? '', deity: filters_?.get('deity') ?? '' })

    const filteredOfferingTags = useMemo(() => {
        return offeringTags.filter(tag => {
            const offering = offerings.find(o => o.url === tag.offering || String(o.id) === String(tag.offering))
            if (filters.category && (!offering || offering.category !== filters.category)) {
                return false
            }
            if (filters.deity && (!offering || !offering.assignedDeities.includes(filters.deity))) {
                return false
            }
            return true
        })
    }, [offeringTags, offerings, filters])

    useEffect(() => {
        dispatch(offeringTagListThunk({}))
    }, [dispatch])

    useEffect(() => {
        if (offerings.length === 0) {
            dispatch(offeringListThunk({}))
        }
    }, [dispatch, offerings.length])

    const addNewHandler: MouseEventHandler<HTMLButtonElement> = () => history.push(`${pathname}/add`)
    const editHandler = (groupId: string | number) => history.push(`${pathname}/edit-group/${encodeURIComponent(String(groupId))}`)
    const handlerOpenDialog = (groupId: string | number) => {
        const groupTags = offeringTags.filter(tag => (tag.tagName ?? tag.name ?? '').trim().toLowerCase() === String(groupId))
        if (groupTags.length > 0) {
            setDeleteItem({
                id: String(groupId),
                name: groupTags[0].tagName ?? groupTags[0].name ?? 'Unknown Tag',
                tags: groupTags
            })
            setDialogOpen(true)
        }
    }

    const handlerCloseDialog = (canDelete?: boolean) => {
        if (canDelete && deleteItem) {
            const deletingGroup = deleteItem
            setDeleteIds([...deleteIds, deletingGroup.id])
            Promise.all(deletingGroup.tags.map(tag => dispatch(offeringTagDeleteThunk(tag))))
                .finally(() => {
                    setDeleteIds(currentDeleteIds => currentDeleteIds.filter(id => id !== deletingGroup.id))
                    setDeleteItem(null)
                    dispatch(offeringTagListThunk({}))
                })
        }
        setDialogOpen(false)
    }

    return (
        <Box>
            <Paper style={{ width: '100%', overflowX: 'auto' }}>
                <OfferingTagToolbar
                    values={filters}
                    changeFilter={(values) => setFilters(values)}
                    addNew={permissions.includes(PERMISSIONS.ADD) ? () => history.push(`${pathname}/add`) : undefined}
                />
                {api.status === apiStatus.LOADING && <LinearProgress />}
                <OfferingTagTable
                    offeringTags={filteredOfferingTags}
                    offerings={offerings}
                    editHandler={permissions.includes(PERMISSIONS.CHANGE) ? editHandler : undefined}
                    deleteHandler={permissions.includes(PERMISSIONS.DELETE) ? handlerOpenDialog : undefined}
                    deletingIds={permissions.includes(PERMISSIONS.DELETE) ? deleteIds : undefined}
                />
            </Paper>
            <Dialog open={Boolean(dialogOpen && deleteItem)}>
                <DialogTitle>{`Are you sure you want to delete ${deleteItem?.name}?`}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`Warning: This tag will be removed from ${deleteItem?.tags.length ?? 0} linked offering${deleteItem?.tags.length === 1 ? '' : 's'}.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="primary" onClick={() => handlerCloseDialog(false)}>
                        <IntlMessages id="common.cancel" />
                    </Button>
                    <RedButton variant="outlined" onClick={() => handlerCloseDialog(true)} autoFocus>
                        <IntlMessages id="common.delete" />
                    </RedButton>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default ListOfferingTag
